import React, { useEffect, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { CheckIcon } from 'lucide-react';
import {
  Tool,
  ToolContent,
  ToolHeader,
  ToolInput,
  ToolOutput,
} from '@cotera/client/v0/components/ai-elements/tool';
import { Icon } from '@cotera/client/app/components/ui';
import { useWhoami } from '@cotera/client/app/stores/org';
import { ResourceType } from '@cotera/api';
import { queryKeys } from '@cotera/client/app/hooks/query-cache-keys';
import { ArtifactSnippet } from '../artifact-snippet';
import type { ToolRendererProps } from './index';
import { coco } from '@cotera/sdk/core';

type AgentArtifactData = {
  agentId?: string;
  agentKey?: string;
};

/**
 * Custom renderer for coco/create-agent and coco/edit-agent (granular saves).
 * The coco/agent-editor sub-agent uses v1 sub-agent chat UI instead.
 * When the tool completes with an agent artifact, invalidates agent queries
 * so the artifact panel refetches the updated agent.
 */
export const CustomizeAgentToolRenderer: React.FC<ToolRendererProps> = ({
  part,
  nested,
}) => {
  const queryClient = useQueryClient();
  const org = useWhoami((s) => s.org);
  const requiresApproval = part.approvalStatus() === 'required';
  const [isExpanded, setIsExpanded] = useState(requiresApproval);
  const toolKey = part.toolKey().toString();
  // `part` gets a fresh identity on every streamed chunk, so this effect re-runs
  // per token. Without this guard the force-`refetchQueries` below re-issues the
  // (uncached) accessible-agents list query at token rate during a Coco
  // create/edit-agent stream. Refetch at most once per completed agent id.
  const refetchedAgentIdRef = React.useRef<string | null>(null);

  useEffect(() => {
    if (!part.hasOutput) {
      return;
    }
    const artifact = part.getArtifact();
    if (artifact === null || artifact.artifactType !== 'agent') {
      return;
    }
    const data = artifact.data as AgentArtifactData | undefined;
    const agentId = data?.agentId ?? artifact.artifactId;
    if (agentId === undefined || agentId === '') {
      return;
    }
    if (refetchedAgentIdRef.current === agentId) {
      return;
    }
    refetchedAgentIdRef.current = agentId;
    void queryClient.refetchQueries({
      queryKey: queryKeys.resource.get({
        orgId: org.id,
        type: ResourceType.Agent,
        id: agentId,
      }),
    });
    void queryClient.refetchQueries({
      queryKey: queryKeys.agents.all({ orgId: org.id }),
    });
  }, [part, queryClient, org.id]);

  const artifact = part.getArtifact();
  const artifactId =
    artifact !== null
      ? artifact.artifactId ?? part.toolCallId
      : part.toolCallId;
  const artifactType = artifact?.artifactType ?? 'agent';
  const toolState = part.toolPart.state;
  const hasOutput = part.hasOutput;

  const body = (
    <>
      <ToolInput input={part.input} />
      <ToolOutput
        errorText={part.toolPart.errorText}
        output={
          hasOutput && artifact !== null ? (
            <div className="flex items-center gap-2">
              <CheckIcon className="h-4 w-4 text-success shrink-0" />
              <ArtifactSnippet
                artifactId={artifactId}
                artifactType={artifactType}
              />
            </div>
          ) : null
        }
      />
    </>
  );

  // Inside a grouped "Run N" row, the row supplies the header/caret, so render
  // only the body — otherwise "Edit Agent" repeats under the group heading.
  if (nested === true) {
    return body;
  }

  return (
    // `px-0` cancels `Tool`'s own `px-4`, since message.tsx already wraps
    // custom renderers in a `px-4` div. Without this the header (and its
    // caret) sits indented 16px past the external-tool rows.
    <Tool className="px-0" open={isExpanded} onOpenChange={setIsExpanded}>
      <ToolHeader
        state={hasOutput ? 'output-available' : toolState}
        title={
          toolKey === coco.COCO_CREATE_AGENT_TOOL_KEY
            ? 'Create Agent'
            : 'Edit Agent'
        }
        type={part.toolPart.type}
        icon={
          <span className="h-4 w-4 flex items-center justify-center [&>svg]:h-4 [&>svg]:w-4">
            <Icon icon="bot" className="h-4 w-4" />
          </span>
        }
      />
      <ToolContent>{body}</ToolContent>
    </Tool>
  );
};
